import React, { useMemo, useState, useRef, useEffect } from "react";
import { useDashboardData } from "./hooks/useDashboardData";
import { useBusinessType } from "./hooks/useBusinessType";
import DashboardLayout from "./DashboardLayout";
import OrderHistory from "./OrderHistory/OrderHistory";
import SetBarChart from "./SetBarChart/SetBarChart";
import StatsOverview from "./StatsOverview";
import { PageTitle } from "src/shared/PageTitle";
import GenerateDailyReport from "./GenerateReport";
import CancelledSummaryCard from "./CancelledSummaryCard";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const Dashboard: React.FC = () => {
  const [isRestoringScroll, setIsRestoringScroll] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [reportGenerated, setReportGenerated] = useState(false);

  // Refs
  const dashboardRef = useRef<HTMLDivElement>(null);
  const previousLoadingRef = useRef(false);
  const restoreScrollTimeoutRef = useRef<NodeJS.Timeout>();

  const businessType = useBusinessType();
  const now = new Date();

  const [selectedMonth, setSelectedMonth] = useState(MONTHS[now.getMonth()]);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const formatLocalDate = (date: Date): string => {
    return date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, "0") + "-" + String(date.getDate()).padStart(2, "0");
  };

  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    const localDate = formatLocalDate(now);

    const monthIndex = MONTHS.indexOf(selectedMonth);

    const startDateObj = new Date(selectedYear, monthIndex, 1);
    const endDateObj = new Date(selectedYear, monthIndex + 1, 0);

    return {
      startDate: formatLocalDate(startDateObj),
      endDate: formatLocalDate(endDateObj),
    };
  }, [selectedMonth, selectedYear]);

  /*** YEAR → BAR CHART + ORDERS ***/
  const {
    dashboardData: yearlyData,
    loading: yearLoading,
    error: yearError,
  } = useDashboardData({
    mode: "year",
    year: selectedYear,
  });

  /*** MONTH → STATS OVERVIEW ***/
  const {
    dashboardData: monthlyData,
    loading: monthLoading,
    error: monthError,
  } = useDashboardData({
    mode: "month",
    startDate,
    endDate,
  });

  // Save scroll position when user is about to change something
  const saveScrollPosition = () => {
    if (dashboardRef.current) {
      const currentScroll = dashboardRef.current.scrollTop;
      setScrollPosition(currentScroll);
    }
  };

  // Restore scroll position after data loads
  useEffect(() => {
    if (previousLoadingRef.current === true && (yearLoading === false || monthLoading === false)) {
      // Data just finished loading
      setIsRestoringScroll(true);

      // Clear any existing timeout
      if (restoreScrollTimeoutRef.current) {
        clearTimeout(restoreScrollTimeoutRef.current);
      }

      // Set a timeout to restore scroll after DOM updates
      restoreScrollTimeoutRef.current = setTimeout(() => {
        if (dashboardRef.current && scrollPosition > 0) {
          dashboardRef.current.scrollTop = scrollPosition;
          setIsRestoringScroll(false);
        }
      }, 100);
    }

    // Update previous loading state
    previousLoadingRef.current = yearLoading || monthLoading;

    // Cleanup timeout on unmount
    return () => {
      if (restoreScrollTimeoutRef.current) {
        clearTimeout(restoreScrollTimeoutRef.current);
      }
    };
  }, [yearLoading, monthLoading, scrollPosition]);

  const handleYearChange = (increment: number) => {
    saveScrollPosition();
    const currentYear = new Date().getFullYear();
    setSelectedYear((prev) => {
      const newYear = prev + increment;
      return newYear <= currentYear ? newYear : prev;
    });
  };

  const handleMonthChange = (month: string) => {
    saveScrollPosition();
    setSelectedMonth(month);
  };

  const handleYearChangeFromSelector = (year: number) => {
    saveScrollPosition();
    setSelectedYear(year);
  };

  // Also add scroll event listener to update position continuously
  useEffect(() => {
    const handleScroll = () => {
      if (dashboardRef.current && !yearLoading && !monthLoading && !isRestoringScroll) {
        setScrollPosition(dashboardRef.current.scrollTop);
      }
    };

    const scrollElement = dashboardRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }
  }, [yearLoading, monthLoading, isRestoringScroll]);

  return (
    <DashboardLayout businessType={businessType}>
      <PageTitle title="- Dashboard" />

      <div ref={dashboardRef} className="thin-scrollbar w-full h-[91.5vh] overflow-y-auto bg-gradient-to-br from-gray-100 to-white">
        {/* Dashboard Header - Always visible */}
        <div className="mb-3 mt-1 px-3 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          {/* Left Section */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
            <p className="text-gray-600 mt-2">Welcome back! Here's what's happening.</p>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-4 flex-wrap md:flex-nowrap">
            {reportGenerated && (
              <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2 max-w-md">
                The report has been sent to your email. For date-wise reports, please visit the Report tab to view or download them.
              </div>
            )}

            <GenerateDailyReport onReportGenerated={() => setReportGenerated(true)} />
          </div>
        </div>
        <div className="pl-3 pr-3">
          <div className="flex flex-col gap-2">
            {/* Stats Overview */}
            <div className="w-full">
              {monthLoading ? (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-2xl flex items-center justify-center z-10">
                  <div className="text-center">
                    <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 rounded-full mb-2" />
                    <p className="text-gray-600 font-medium">Loading monthly data...</p>
                  </div>
                </div>
              ) : (
                monthlyData && (
                  <StatsOverview dashboardData={monthlyData} selectedMonth={selectedMonth} selectedYear={selectedYear} onMonthChange={handleMonthChange} onYearChange={handleYearChangeFromSelector} />
                )
              )}
            </div>

            {/* Cancelled Summary */}
            {/* {monthlyData?.cancelled_summary && (
              <div className="w-full mb-2">
                <CancelledSummaryCard data={monthlyData.cancelled_summary} />
              </div>
            )} */}
          </div>

          {/* YEAR STATS */}
          <div className="relative">
            {yearLoading ? (
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 rounded-2xl flex items-center justify-center">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                  <p className="text-gray-600 font-medium">Loading yearly data...</p>
                </div>
              </div>
            ) : yearlyData ? (
              <>
                <OrderHistory dashboardData={yearlyData} businessType={businessType} />
                <SetBarChart dashboardData={yearlyData} selectedYear={selectedYear} onYearChange={handleYearChange} />
              </>
            ) : null}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
